import React, { useEffect, useRef, useState } from "react";
import { X, Download, Sparkles, Check, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { lockBodyScroll } from "@/lib/lockBodyScroll";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Pop-up listing every completed AI-edit job for one fotoreeks photo.
 * Admin can scrub through history (oldest → newest), download any version,
 * re-activate ("Maak actief") any previous AI edit, OR replace either the
 * original or a specific AI version with a freshly uploaded file (handy
 * when the admin wants to Photoshop a frame before further AI work).
 *
 * Props:
 *   - open            : boolean
 *   - onClose         : () => void
 *   - requestId       : string           needed for replace endpoints
 *   - photoIndex      : number (0-based)
 *   - originalUrl     : string | null    pre-AI original (slot 0 if it exists)
 *   - currentUrl      : string           currently active project photo URL
 *   - jobs            : AiEditJob[]      completed jobs for THIS photo
 *   - addressLabel    : string           used in download filename
 *   - onActivated     : (job) => void    parent updates ai_photo_urls / re-loads
 *   - onReplaced      : () => void       parent refresh after any replace
 */
export default function AiEditHistoryModal({
    open,
    onClose,
    requestId,
    photoIndex,
    originalUrl,
    currentUrl,
    jobs = [],
    addressLabel = "",
    onActivated,
    onReplaced,
}) {
    const [activatingId, setActivatingId] = useState(null);
    const [replacing, setReplacing] = useState(null); // "original" | jobId
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const originalFileRef = useRef(null);
    const jobFileRefs = useRef({});

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", handler);
        const release = lockBodyScroll(true);
        return () => {
            window.removeEventListener("keydown", handler);
            release();
        };
    }, [open, onClose]);

    if (!open) return null;

    const slugify = (s) => (s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s.-]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "foto";

    const dlName = (label, ext = "jpg") => {
        const addr = slugify(addressLabel);
        const n = String((photoIndex ?? 0) + 1).padStart(2, "0");
        return `${addr ? addr + " - " : ""}foto ${n} - ${label} - SocialFactoryAI.${ext}`;
    };

    // Oldest → newest so history flows naturally left-to-right
    const completed = [...(jobs || [])]
        .filter((j) => j.status === "completed" && j.result_url)
        .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));

    const activate = async (job) => {
        if (job.result_url === currentUrl) {
            toast.info("Deze versie is al actief");
            return;
        }
        setActivatingId(job.id);
        try {
            const { data } = await api.post(`/ai-edit-jobs/${job.id}/approve`);
            toast.success("Versie geactiveerd");
            onActivated?.(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Activeren mislukt");
        }
        setActivatingId(null);
    };

    const activateOriginal = async () => {
        if (!requestId || photoIndex == null) return;
        setActivatingId("original");
        try {
            await api.post(`/requests/${requestId}/ai-photos/${photoIndex}/activate-original`);
            toast.success("Origineel is nu de actieve versie");
            onReplaced?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Activeren mislukt");
        }
        setActivatingId(null);
    };

    // Upload a file and return the resulting public URL. Used by both the
    // "vervang origineel" and "vervang AI-versie" flows.
    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.url;
    };

    const replaceOriginal = async (file) => {
        if (!file || !requestId || photoIndex == null) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Enkel afbeeldingen toegestaan");
            return;
        }
        setReplacing("original");
        try {
            const newUrl = await uploadFile(file);
            await api.post(
                `/requests/${requestId}/ai-photos/${photoIndex}/replace-original`,
                { new_photo_url: newUrl },
            );
            toast.success("Origineel bijgewerkt — gebruik 'Bewerk met AI' om er een nieuwe versie van te maken");
            onReplaced?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Vervangen mislukt");
        }
        setReplacing(null);
    };

    const replaceAiVersion = async (job, file) => {
        if (!file || !requestId || photoIndex == null) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Enkel afbeeldingen toegestaan");
            return;
        }
        setReplacing(job.id);
        try {
            const newUrl = await uploadFile(file);
            // If this version is currently active, also update ai_photo_urls
            // via the existing replace endpoint so the gallery picks it up.
            if (job.result_url === currentUrl) {
                await api.post(
                    `/requests/${requestId}/ai-photos/${photoIndex}/replace`,
                    { new_photo_url: newUrl },
                );
            } else {
                // Otherwise just patch the job's result_url so this historical
                // version reflects the photoshopped file. Backend re-uses the
                // existing AiEditManualUrlBody schema (field name `fal_url`).
                await api.patch(`/ai-edit-jobs/${job.id}`, { fal_url: newUrl });
            }
            toast.success("AI-versie bijgewerkt");
            onReplaced?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Vervangen mislukt");
        }
        setReplacing(null);
    };

    const deleteVersion = async (job) => {
        setDeletingId(job.id);
        try {
            await api.delete(`/ai-edit-jobs/${job.id}`);
            toast.success("AI-versie verwijderd");
            setConfirmDeleteId(null);
            onReplaced?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verwijderen mislukt");
        }
        setDeletingId(null);
    };

    return (
        <div
            className="fixed inset-0 z-[210] bg-black/90 flex items-start justify-center overflow-y-auto p-4 lg:p-8"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="ai-edit-history-modal"
        >
            <div className="w-full max-w-6xl bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md shadow-2xl shadow-black/60">
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-200 bg-[#FFFFFF]/95 backdrop-blur-md">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="h-4 w-4 text-[#E5A93D] flex-shrink-0" />
                        <h3 className="font-display font-bold text-base tracking-tight truncate">
                            AI history — foto #{(photoIndex ?? 0) + 1}
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-[#E5A93D] bg-[#E5A93D]/10 border border-[#E5A93D]/30 rounded-sm px-1.5 py-0.5 flex-shrink-0">
                            {completed.length} versie{completed.length === 1 ? "" : "s"}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center h-9 w-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition"
                        data-testid="ai-edit-history-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {completed.length === 0 && !originalUrl && (
                        <div className="text-sm text-zinc-500 text-center py-12">
                            Nog geen AI-bewerkingen voor deze foto.
                        </div>
                    )}

                    {/* Grid of versions */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Original — always shown as anchor when it exists */}
                        {originalUrl && (
                            <div className="bg-[#FAFAFA] border border-zinc-200 rounded-sm overflow-hidden flex flex-col" data-testid="ai-edit-history-original">
                                <div className="relative aspect-[4/3] bg-zinc-50">
                                    <img src={`${BACKEND}${originalUrl}`} alt="Origineel" className="w-full h-full object-cover" />
                                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-black/80 text-zinc-100 rounded-sm px-1.5 py-0.5">
                                        Origineel
                                    </span>
                                    {originalUrl === currentUrl && (
                                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-black rounded-sm px-1.5 h-5">
                                            <Check className="h-3 w-3" /> Actief
                                        </span>
                                    )}
                                </div>
                                <div className="p-2 flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-zinc-500">Pre-AI versie</span>
                                    <div className="flex items-center gap-1.5">
                                        <a
                                            href={`${BACKEND}${originalUrl}?dl=${encodeURIComponent(dlName("origineel"))}`}
                                            download={dlName("origineel")}
                                            className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95"
                                            data-testid="ai-edit-history-download-original"
                                        >
                                            <Download className="h-3 w-3" /> JPG
                                        </a>
                                        {originalUrl !== currentUrl && (
                                            <button
                                                type="button"
                                                onClick={activateOriginal}
                                                disabled={activatingId === "original"}
                                                className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold rounded-sm transition active:scale-95"
                                                data-testid="ai-edit-history-activate-original"
                                                title="Zet het origineel terug als actieve foto in de reeks"
                                            >
                                                {activatingId === "original" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                                Maak actief
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => originalFileRef.current?.click()}
                                            disabled={replacing === "original"}
                                            className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-[#E5A93D] hover:bg-[#E5A93D]/90 text-black font-medium rounded-sm transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                                            data-testid="ai-edit-history-replace-original"
                                            title="Vervang de pre-AI versie (bv. een Photoshop-bewerking)"
                                        >
                                            {replacing === "original"
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <><Upload className="h-3 w-3" /> Vervang</>}
                                        </button>
                                        <input
                                            ref={originalFileRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                e.target.value = "";
                                                if (f) replaceOriginal(f);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* AI versions */}
                        {completed.map((j, i) => {
                            const isActive = j.result_url === currentUrl;
                            const versionLabel = `v${i + 1}`;
                            const ts = new Date(j.created_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" });
                            return (
                                <div
                                    key={j.id}
                                    className={`bg-[#FAFAFA] border rounded-sm overflow-hidden flex flex-col ${isActive ? "border-emerald-500/50" : "border-zinc-200"}`}
                                    data-testid={`ai-edit-history-item-${j.id}`}
                                >
                                    <div className="relative aspect-[4/3] bg-zinc-50">
                                        <img src={`${BACKEND}${j.result_url}`} alt={versionLabel} className="w-full h-full object-cover" />
                                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E5A93D] text-black font-bold rounded-sm px-1.5 py-0.5">
                                            <Sparkles className="h-3 w-3" /> {versionLabel}
                                        </span>
                                        {isActive && (
                                            <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-black rounded-sm px-1.5 h-5">
                                                <Check className="h-3 w-3" /> Actief
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-2 space-y-2">
                                        <div className="text-[11px] text-zinc-500 flex items-center justify-between gap-2 flex-wrap">
                                            <span>{ts}</span>
                                            {j.cost_eur != null && <span className="font-mono">€{Number(j.cost_eur).toFixed(2)}</span>}
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <a
                                                href={`${BACKEND}${j.result_url}?dl=${encodeURIComponent(dlName(`AI-${versionLabel}`))}`}
                                                download={dlName(`AI-${versionLabel}`)}
                                                className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95"
                                                data-testid={`ai-edit-history-download-${j.id}`}
                                            >
                                                <Download className="h-3 w-3" /> Download
                                            </a>
                                            {!isActive && (
                                                <button
                                                    type="button"
                                                    onClick={() => activate(j)}
                                                    disabled={activatingId === j.id}
                                                    className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-[#E5A93D] hover:bg-[#C8922C] disabled:opacity-60 text-black font-bold rounded-sm transition active:scale-95"
                                                    data-testid={`ai-edit-history-activate-${j.id}`}
                                                >
                                                    {activatingId === j.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                                    Maak actief
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => jobFileRefs.current[j.id]?.click()}
                                                disabled={replacing === j.id}
                                                className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                                                data-testid={`ai-edit-history-replace-${j.id}`}
                                                title={isActive ? "Vervang deze actieve AI-versie (bv. Photoshop bewerking)" : "Vervang deze AI-versie in de history"}
                                            >
                                                {replacing === j.id
                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                    : <><Upload className="h-3 w-3" /> Vervang</>}
                                            </button>
                                            <input
                                                ref={(el) => { jobFileRefs.current[j.id] = el; }}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    e.target.value = "";
                                                    if (f) replaceAiVersion(j, f);
                                                }}
                                            />
                                            {!isActive && (
                                                confirmDeleteId === j.id ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => deleteVersion(j)}
                                                            disabled={deletingId === j.id}
                                                            className="inline-flex items-center gap-1.5 text-[11px] px-2 h-7 bg-red-600 hover:bg-red-700 text-white font-bold rounded-sm transition active:scale-95 disabled:opacity-60"
                                                            data-testid={`ai-edit-history-delete-confirm-${j.id}`}
                                                        >
                                                            {deletingId === j.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                                            Zeker?
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfirmDeleteId(null)}
                                                            className="inline-flex items-center text-[11px] px-2 h-7 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition"
                                                            data-testid={`ai-edit-history-delete-cancel-${j.id}`}
                                                        >
                                                            Nee
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmDeleteId(j.id)}
                                                        className="inline-flex items-center justify-center h-7 w-7 bg-zinc-100 hover:bg-red-600 text-zinc-500 hover:text-white rounded-sm transition active:scale-95"
                                                        data-testid={`ai-edit-history-delete-${j.id}`}
                                                        title="Verwijder deze AI-versie definitief"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                )
                                            )}
                                        </div>
                                        {j.prompt && (
                                            <details className="text-[10px] text-zinc-500">
                                                <summary className="cursor-pointer text-zinc-500 hover:text-zinc-600 select-none">Prompt</summary>
                                                <div className="mt-1 font-mono leading-snug bg-black/40 border border-white/5 rounded-sm p-1.5">
                                                    {j.prompt}
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
